import React, { useState, useEffect } from "react";
import { Card, Typography, Form, Input, Button, Space, List, Tag, Divider, Empty, Alert, Spin, Descriptions } from "antd";
import { SearchOutlined, LinkOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { flightOperationApi } from "../../../../../api/flightOperationFlightApi";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;

/**
 * MVT航班关联处理器
 * V5.0 更新：标准化处理器接口
 * 处理MVT报文解析成功但未匹配到航班的场景
 */
const MvtFlightLinker = ({ 
    actionItem, 
    onConfirm, 
    onReject, 
    loading: externalLoading = false, 
    readOnly = false,
    // 向后兼容的额外参数
    onLinkAndConfirm 
}) => {
    const [searchForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [flights, setFlights] = useState([]);
    const [selectedFlight, setSelectedFlight] = useState(null);

    // 从actionItem中获取解析出的MVT数据
    const parsedData = actionItem.parsed_data || actionItem.suggested_payload?.context_data_summary?.parsed_mvt_data || {};
    const rawContent = actionItem.raw_input_content || "";

    // 解析出航班基本信息，用于搜索建议和显示
    const flightNumber = parsedData.flight_number || parsedData.flightNumber;
    const flightDate = parsedData.flight_date || parsedData.flightDate;
    const originAirport = parsedData.departure_airport || parsedData.origin;
    const destinationAirport = parsedData.arrival_airport || parsedData.destination;

    // 初始化搜索表单
    useEffect(() => {
        if (flightNumber) {
            searchForm.setFieldsValue({ flight_number: flightNumber });
            // 如果有航班号，自动搜索
            handleSearch();
        }
    }, [flightNumber, searchForm]);

    // 搜索航班
    const handleSearch = async () => {
        setLoading(true);
        setFlights([]);
        setSelectedFlight(null);

        try {
            const values = searchForm.getFieldsValue();
            const searchParams = {};

            if (values.flight_number) searchParams.flight_number = values.flight_number;

            // 如果有日期，添加日期范围（前后3天）
            if (flightDate) {
                const date = dayjs(flightDate);
                searchParams.departure_date_start = date.subtract(3, "day").format("YYYY-MM-DD");
                searchParams.departure_date_end = date.add(6, "day").format("YYYY-MM-DD"); // +3天从基准日期算
            }

            // 如果有出发/到达机场，也加入搜索条件
            if (originAirport) searchParams.departure_airport = originAirport;
            if (destinationAirport) searchParams.arrival_airport = destinationAirport;

            const response = await flightOperationApi.searchFlightOperations(searchParams);
            setFlights(response.items || []);
        } catch (error) {
            console.error("搜索航班失败:", error);
        } finally {
            setLoading(false);
        }
    };

    // 选择航班
    const handleSelectFlight = (flight) => {
        setSelectedFlight(flight);
    };

    // 关联并确认
    const handleLinkAndConfirm = () => {
        if (!selectedFlight) return;

        // 构建参数以符合后端ChangeDispatcherService的期望
        // 参考 MVTProcessingAgent._format_suggested_payload 的输出结构
        const linkParams = {
            flight_operation_id: selectedFlight.id,
            
            // IATA标准时间字段 (从parsedData映射)
            // 注意: 假设parsedData中的时间字段已经是UTC格式
            new_atd_utc: parsedData.off_block_time || null,
            new_atd_icao_utc: parsedData.airborne_time || null,
            new_eta_utc: parsedData.estimated_arrival_time || null, // MVT 可能包含ETA (EA)
            new_etd_utc: parsedData.estimated_departure_time || null, // MVT 可能包含ETD (ED)
            new_ata_utc: parsedData.on_block_time || null,
            new_ata_icao_utc: parsedData.touchdown_time || null,
            
            delay_code: parsedData.delay_code || null,
            // 其他从parsedData中可以直接映射到action_parameters顶层的字段...
            // 例如: flight_status: parsedData.flight_status (如果解析器提供)

            // 将所有原始解析数据放入 mvt_full_data
            mvt_full_data: { ...parsedData }
        };

        onLinkAndConfirm(linkParams);
    };

    // 渲染原始MVT报文
    const renderMvtRawContent = () => {
        if (!rawContent) return null;
        
        return (
            <Card 
                size="small" 
                title="原始MVT报文" 
                style={{ marginBottom: 16 }}
            >
                <div
                    style={{
                        fontFamily: "monospace",
                        fontSize: "14px",
                        whiteSpace: "pre-wrap",
                        background: "#f5f5f5",
                        padding: "12px",
                        borderRadius: "4px",
                        maxHeight: "150px",
                        overflowY: "auto"
                    }}
                >
                    {rawContent}
                </div>
            </Card>
        );
    };

    // 渲染MVT解析数据摘要
    const renderMvtSummary = () => {
        const items = [];

        if (flightNumber) items.push({ label: "航班号", content: flightNumber });
        if (flightDate) items.push({ label: "日期", content: dayjs(flightDate).format("YYYY-MM-DD") });

        // 处理起飞信息
        if (parsedData.off_block_time) {
            items.push({
                label: "撤轮挡 (Off-block)",
                content: dayjs.utc(parsedData.off_block_time).format("YYYY-MM-DD HH:mm") + "Z",
            });
        }
        if (parsedData.airborne_time) {
            items.push({
                label: "实际离地 (Airborne)",
                content: dayjs.utc(parsedData.airborne_time).format("YYYY-MM-DD HH:mm") + "Z",
            });
        }

        // 处理落地信息
        if (parsedData.touchdown_time) {
            items.push({
                label: "实际落地 (Touch-down)",
                content: dayjs.utc(parsedData.touchdown_time).format("YYYY-MM-DD HH:mm") + "Z",
            });
        }
        if (parsedData.on_block_time) {
            items.push({
                label: "上轮档 (On-block)",
                content: dayjs.utc(parsedData.on_block_time).format("YYYY-MM-DD HH:mm") + "Z",
            });
        }

        // 延误信息
        if (parsedData.delay_code) {
            items.push({ label: "延误代码", content: parsedData.delay_code });
        }
        if (parsedData.delay_duration) {
            items.push({ label: "延误时间", content: `${parsedData.delay_duration}分钟` });
        }

        // 机场信息
        if (originAirport) items.push({ label: "出发机场", content: originAirport });
        if (destinationAirport) items.push({ label: "到达机场", content: destinationAirport });

        // 补充信息
        if (parsedData.supplementary_info) {
            items.push({ label: "补充信息", content: parsedData.supplementary_info });
        }

        // 未解析内容
        if (parsedData.unparsed_lines && parsedData.unparsed_lines.length > 0) {
            items.push({
                label: "未解析内容",
                content: parsedData.unparsed_lines.join("\n"),
                span: 2, // 占两列
            });
        }

        return (
            <Descriptions title="MVT报文已解析的数据" bordered size="small" column={2} style={{ marginBottom: 24 }}>
                {items.map((item, index) => (
                    <Descriptions.Item key={index} label={item.label} span={item.span || 1}>
                        {item.content}
                    </Descriptions.Item>
                ))}
            </Descriptions>
        );
    };

    // 渲染航班搜索结果
    const renderFlightList = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Spin tip="正在搜索匹配航班..." />
                </div>
            );
        }

        if (flights.length === 0) {
            return (
                <Empty 
                    description={
                        <div>
                            <p>未找到匹配航班</p>
                            <small style={{ color: "#999" }}>请调整搜索条件或稍后手动关联航班</small>
                        </div>
                    } 
                    style={{ margin: "20px 0" }} 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            );
        }

        return (
            <List
                itemLayout="horizontal"
                dataSource={flights}
                renderItem={(flight) => (
                    <List.Item
                        onClick={() => handleSelectFlight(flight)}
                        style={{
                            cursor: "pointer",
                            background: selectedFlight && selectedFlight.id === flight.id ? "#f0f8ff" : "white",
                            padding: "8px",
                            borderLeft: selectedFlight && selectedFlight.id === flight.id ? "4px solid #1890ff" : "none",
                        }}
                    >
                        <List.Item.Meta
                            title={
                                <Space>
                                    <Text strong>{flight.flight_number}</Text>
                                    <Text type="secondary">
                                        {flight.departure_airport}-{flight.arrival_airport}
                                    </Text>
                                    {flight.status && <Tag color="blue">{flight.status}</Tag>}
                                </Space>
                            }
                            description={
                                <>
                                    <div>
                                        <Text type="secondary">
                                            计划: {dayjs.utc(flight.std).format("MM-DD HH:mm")}Z - {dayjs.utc(flight.sta).format("MM-DD HH:mm")}Z
                                        </Text>
                                    </div>
                                    {flight.aircraft_reg && (
                                        <div>
                                            <Text type="secondary">飞机: {flight.aircraft_reg}</Text>
                                        </div>
                                    )}
                                </>
                            }
                        />
                    </List.Item>
                )}
            />
        );
    };

    // 渲染未匹配提示
    const renderNoMatchPrompt = () => {
        return (
            <Alert
                message="MVT报文未匹配到系统航班"
                description={
                    <div>
                        <p>系统无法自动匹配到对应航班，可能原因：</p>
                        <ul style={{ paddingLeft: "20px", margin: "8px 0" }}>
                            <li>航班号与系统记录不匹配</li>
                            <li>航班日期与系统记录不匹配</li>
                            <li>航线与系统记录不匹配</li>
                        </ul>
                        <p>请从下方搜索并选择要关联的航班</p>
                    </div>
                }
                type="warning"
                icon={<InfoCircleOutlined />}
                showIcon
                style={{ marginBottom: 16 }}
            />
        );
    };

    return (
        <div>
            {/* 展示原始MVT报文 */}
            {renderMvtRawContent()}
            
            {/* 未匹配提示 */}
            {renderNoMatchPrompt()}

            {/* MVT报文解析数据摘要 */}
            {renderMvtSummary()}

            {/* 航班搜索区域 */}
            <Card title="选择关联航班" style={{ marginBottom: 16 }}>
                <Form form={searchForm} layout="inline" onFinish={handleSearch}>
                    <Form.Item name="flight_number" label="航班号" style={{ marginBottom: 16 }}>
                        <Input placeholder="请输入航班号" style={{ width: "180px" }} />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 16 }}>
                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                            搜索航班
                        </Button>
                    </Form.Item>
                </Form>

                <Divider style={{ margin: "12px 0" }} />

                {/* 航班列表 */}
                {renderFlightList()}
            </Card>

            {/* 提交按钮区域 */}
            {selectedFlight && (
                <>
                    <Alert
                        message="准备关联以下航班"
                        description={
                            <>
                                <Text strong>{selectedFlight.flight_number}</Text> ({selectedFlight.departure_airport}-{selectedFlight.arrival_airport})， 计划起飞: {dayjs.utc(selectedFlight.std).format("YYYY-MM-DD HH:mm")}Z
                            </>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <Button type="primary" icon={<LinkOutlined />} onClick={handleLinkAndConfirm} style={{ marginBottom: 8 }}>
                        关联并确认
                    </Button>
                </>
            )}
        </div>
    );
};

export default MvtFlightLinker;
